import React from "react";
import { Stepper, SelectCard, Typography, TagFields, Checkbox } from "@ann/web-components";
import {
  UserDetailsContainer,
  EmailSection,
  InvitationPanel,
  SendInvitationRow,
  CheckboxColumn,
  SendInvitationCopy,
  SelectCardContainer,
  StepperContainer,
  UserTypeSection,
} from "./UserDetails.styled";
import { INVITE_STEPS, STEP_IDS } from "../../constants/inviteSteps";

const UserDetails = ({
  selected,
  setSelected,
  emails,
  setEmails,
  inviteError,
  setInviteError,
  sendInvitation,
  setSendInvitation,
}) => {

  const handleEmailChange = (newEmails) => {
    // Use functional update to avoid closure issues
    setEmails((prevEmails) => {
      // Validate that the new array is valid and not corrupted
      if (!Array.isArray(newEmails)) {
        console.error('Invalid emails array received:', newEmails);
        return prevEmails;
      }
      
      // Check if this is a removal (new array is shorter)
      if (newEmails.length < prevEmails.length) {
        const removedCount = prevEmails.length - newEmails.length;
        
        // If more than one email is being removed, something is wrong
        // We should only remove one at a time
        if (removedCount > 1) {
          console.warn('Multiple emails being removed at once, correcting...');
          // Find which email(s) were removed and only remove the first one
          const removedEmails = prevEmails.filter(email => !newEmails.includes(email));
          
          // Remove only the first removed email
          if (removedEmails.length > 0) {
            const firstRemovedIndex = prevEmails.indexOf(removedEmails[0]);
            return prevEmails.filter((_, index) => index !== firstRemovedIndex);
          }
        }
      }
      
      // For additions or single removals, use the new array directly
      return newEmails;
    });
    
    if (inviteError) {
      setInviteError(null);
    }
  };

  return (
    <UserDetailsContainer>
      <StepperContainer>
        <Stepper
          activeStep={STEP_IDS.USER_DETAILS}
          onSelectStep={() => { }}
          steps={INVITE_STEPS}
        />
      </StepperContainer>

      <EmailSection aria-label="Email addresses">
        <Typography category="title" size="small" weight="bold">
          Email addresses
        </Typography>
        <Typography category="body" size="medium" weight="regular">
          Enter one or more addresses. Press Enter after each. You can choose whether to email
          them an invitation in the next section.
        </Typography>

        <TagFields
          label="Email Address*"
          onChange={handleEmailChange}
          placeholder="Enter email address (e.g., user@domain.com)"
          size="small"
          state="default"
          value={emails}
          validation="email"
          onValidationError={setInviteError}
        />
      </EmailSection>

      <InvitationPanel>
        <Typography category="title" size="small" weight="bold">
          Invitation
        </Typography>
        <SendInvitationRow>
          <CheckboxColumn>
            <Checkbox
              checked={sendInvitation}
              onChange={() => setSendInvitation((v) => !v)}
              size="small"
            />
          </CheckboxColumn>
          <SendInvitationCopy>
            <Typography category="body" size="medium" weight="regular">
              Send invitation email to new users
            </Typography>
            <Typography category="body" size="small" weight="regular" color="body">
              When off, accounts are created without sending an invitation. You can send one later
              from the Users list.
            </Typography>
          </SendInvitationCopy>
        </SendInvitationRow>
      </InvitationPanel>

      <UserTypeSection aria-label="User type">
        <Typography category="title" size="small" weight="bold">
          Select user type
        </Typography>

        <SelectCardContainer id="select-card-container">
          <SelectCard.Group kind="radio" value={selected} onChange={setSelected} direction="row" itemsPerRow={3}>
            <SelectCard id="user">
              <SelectCard.Icon name="person" />
              <SelectCard.Title value="User" />
              <SelectCard.Content value="Access is controlled by roles and permissions." />
            </SelectCard>
            <SelectCard id="admin">
              <SelectCard.Icon name="adminPanelSettings" />
              <SelectCard.Title value="Admin" />
              <SelectCard.Content value="Manage most settings and users." />
            </SelectCard>
            <SelectCard id="superadmin">
              <SelectCard.Icon name="shield" />
              <SelectCard.Title value="Super Admin" />
              <SelectCard.Content value="Full access to all settings." />
            </SelectCard>
          </SelectCard.Group>
        </SelectCardContainer>
      </UserTypeSection>
    </UserDetailsContainer>
  );
};

export default UserDetails;

